
import useUiController from "../global/hooks/useUiController";
import {
    useSelector } from "react-redux";
import { useEffect,
    useState } from "react";
import {
  CART_INITIAL_STATE,
  createWrappedTextColumn,
  CREDIT,
  formatDateInReact,
  formatDateInSnp,
  isEmpty,
  printDebugLog,
  SUCCESS
} from "../../constants/constant";
import SnpBaseLayout from "../global/SnpBaseLayout";
import { Grid, TextField } from "../ui";
import "./paymentinfo.css";
import { snpAxios } from "../global/api";
import CustomDataGrid from "../global/CustomDataGrid";
import SnpButton from "../global/SnpButton";


const PaymentInformation = () => {

    const {
        loader,
        setLoader,
        snackbarConfig,
        showSnackbar,
        closeSnackbar,
        dialogConfig,
        showDialog,
        closeDialog,
    } = useUiController();

    const todayFormattedDate = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    const storeApiUrl = useSelector(state => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL);
    const storeActiveShopHash = useSelector(state => state.setActiveShopHashReducer.activeShopHash);

    const [onlineBalanceWidget, setOnlineBalanceWidget] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [data, setData] = useState([]);

    const paymentSummary = [
        {
            key: "total-balance",
            title: onlineBalanceWidget?.title ?? "JcuPay Total",
            amount: onlineBalanceWidget?.totalBal ?? 0,
            background: "#619a59",
        },
        {
            key: "bank-deduction",
            title: "Bank Deduction",
            amount: onlineBalanceWidget?.totalBankDeduction ?? 0,
            background: "#9a5959",
        },
        {
            key: "final-amount",
            title: "Final Amount",
            amount: onlineBalanceWidget?.finalAmount ?? 0,
            background: "#3f728f",
        },
    ];

    const paymentSummaryDateRange = [onlineBalanceWidget?.startDate, onlineBalanceWidget?.endDate]
        .filter(Boolean)
        .join(" - ");

    const isInvalidDateRange = (selectedStartDate, selectedEndDate) => (
        Boolean(selectedStartDate && selectedEndDate && selectedStartDate >= selectedEndDate)
    );

    const showDateRangeWarning = () => {
        showDialog({
            ...dialogConfig,
            title: "Invalid date range",
            subTitle: "Enter Start Date must be earlier than Enter End Date.",
            negativeReq: true,
            negativeAction: "OK",
            positiveReq: false,
            outSideClickDismiss: false,
            cancelExtra: true,
            handleClose: closeDialog,
        });
    };

    const handleStartDateChange = (event) => {
        const selectedStartDate = event.target.value;
        setStartDate(selectedStartDate);
        if (isInvalidDateRange(selectedStartDate, endDate)) {
            showDateRangeWarning();
        }
    };

    const handleEndDateChange = (event) => {
        const selectedEndDate = event.target.value;
        setEndDate(selectedEndDate);
        if (isInvalidDateRange(startDate, selectedEndDate)) {
            showDateRangeWarning();
        }
    };

    const columns = [
        createWrappedTextColumn({
            field: "billNo",
            headerName: "Bill No",
            width: 100,
            fontSize: "18px",
            fontWeight: "bold"
        }),
        { field: "pg_id", headerName: "Payment Id", width: 160, filterable: true },
        createWrappedTextColumn({
            field: "paidAt",
            headerName: "Paid At",
            width: 160,
        }),
        createWrappedTextColumn({
            field: "settleAt",
            headerName: "Settled At",
            width: 160,
        }),
        createWrappedTextColumn({
            field: "paidAmount",
            headerName: "Amt",
            width: 90,
            fontWeight: "bold",
            color_condition: (value,row) =>
                row.drCr === CREDIT
                    ? "#0f633b"
                    : "#7a1904",
        }),
        createWrappedTextColumn({
            field: "paymentStatus",
            headerName: "Payment Status",
            width: 160,
            fontWeight: "bold",
            color_condition: (value,row) =>
                row.drCr === CREDIT
                    ? "#0f633b"
                    : "#7a1904",
            fontSize: "18px"
        }),

        { field: "method", headerName: "Method", width: 100, filterable: true },
        createWrappedTextColumn({
            field: "upiVpa",
            headerName: "Upi/Vpa",
            width: 110,
        }),
        { field: "wallet_id", headerName: "Wallet Id", width: 120, filterable: true },
        { field: "card", headerName: "Card", width: 140, filterable: true },
        {
            field: "bank_name", headerName: "Bank Name", width: 250
            , filterable: true
        },
        { field: "paid_by", headerName: "Paid By", width: 300, filterable: true },
        {
            field: "paymentRef", headerName: "Other Details", width: 250
            , filterable: true
        },
    ];

    useEffect(() => {
        printDebugLog("PaymentInformation");
        getOnlinePaymentDetails();
    }, [storeApiUrl, storeActiveShopHash]);

    const getOnlinePaymentDetails = async () => {
        if (isInvalidDateRange(startDate, endDate)) {
            showDateRangeWarning();
            return;
        }
        if (isEmpty(startDate) || isEmpty(endDate)) {
            printDebugLog("Start Date and End Date are required.");
            return;
        }

        const catgUrl = `${storeApiUrl.BACKEND_BASE_URL}accounting/online-payment-details`;
        const data = {
            jcJson: {
                apiToken: CART_INITIAL_STATE.apiToken,
                shopHash: storeActiveShopHash.id,
                endDate: formatDateInSnp(endDate),
                startDate: formatDateInSnp(startDate),
            }
        };
        setLoader(true);
        snpAxios
            .post(catgUrl, data)
            .then((response) => {
                setLoader(false);
                if (response.data.status === SUCCESS) {
                    setOnlineBalanceWidget({ ...response.data.jcJson });
                    setData(response.data.jcJson.fulldata ?? []);
                }
            })
            .catch((error) => {
                setLoader(false);
                console.log("Error : ", error);
                setOnlineBalanceWidget(null);
                //window.location.href = "#/error";
            });
    };

    return (
        <SnpBaseLayout
            loader={loader}
            snackbarConfig={{ ...snackbarConfig, onClose: closeSnackbar }}
            dialogConfig={dialogConfig}
        >
            <Grid container spacing={1}>
                <Grid item xs={12} zeroMinWidth>
                    <Grid container spacing={1}>
                        {paymentSummary.map((summary) => (
                            <Grid item xs={12} sm={4} md={4} zeroMinWidth key={summary.key}>
                                <div
                                    className="online-bal-item accounting-online-bal-item"
                                    style={{ background: summary.background }}
                                    title={summary.title}
                                >
                                    <p style={{
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        color: "white",
                                        fontSize: "clamp(14px, 1.6vw, 19px)",
                                        lineHeight: 1.35,
                                        margin: 0,
                                        width: "100%"
                                    }}>{`${summary.title}\n₹${summary.amount}${paymentSummaryDateRange ? `\n${paymentSummaryDateRange}` : ""}`}</p>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={6} md={6} zeroMinWidth>
                            <TextField
                                type="date"
                                id="startDate"
                                name="Start Date"
                                variant="filled"
                                size="small"
                                label="Enter Start Date"
                                value={formatDateInReact(startDate)}
                                fullWidth
                                onChange={handleStartDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} zeroMinWidth>
                            <TextField
                                type="date"
                                id="endDate"
                                name="End Date"
                                variant="filled"
                                size="small"
                                label="Enter End Date"
                                value={formatDateInReact(endDate)}
                                fullWidth
                                onChange={handleEndDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <SnpButton variant="contained" onPointerDown={() => getOnlinePaymentDetails()}>
                                Submit
                            </SnpButton>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <CustomDataGrid
                        getRowId={(row) => row.accountingEntryId ?? row.pg_id ?? row.billNo}
                        rows={data}
                        columns={columns}
                    />
                </Grid>

            </Grid>
        </SnpBaseLayout>
    )
}

export default PaymentInformation;
